import { useCallback, type FC } from 'react';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useDropzone } from 'react-dropzone';

type BulkImageDropAreaProps = {
    onFiles: (files: File[], rejectedCount: number) => void;
    accept: string;
    disabled?: boolean;
    title: string;
    hint: string;
    activeHint: string;
};

export const BulkImageDropArea: FC<BulkImageDropAreaProps> = ({
    onFiles,
    accept,
    disabled = false,
    title,
    hint,
    activeHint,
}) => {
    const onDrop = useCallback(
        (acceptedFiles: File[], fileRejections: { file: File }[]) => {
            if (acceptedFiles.length === 0 && fileRejections.length === 0) {
                return;
            }
            onFiles(acceptedFiles, fileRejections.length);
        },
        [onFiles],
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept,
        multiple: true,
        disabled,
        noKeyboard: false,
    });

    return (
        <Box
            {...getRootProps()}
            sx={(theme) => ({
                p: 4,
                textAlign: 'center',
                border: `2px dashed ${isDragActive ? theme.palette.primary.main : theme.palette.divider}`,
                borderRadius: 3,
                backgroundColor: isDragActive
                    ? alpha(theme.palette.primary.main, 0.1)
                    : theme.palette.grey[50],
                opacity: disabled ? 0.45 : 1,
                cursor: disabled ? 'not-allowed' : 'pointer',
                transition: 'border-color 0.15s ease, background-color 0.15s ease',
                outline: 'none',
                userSelect: 'none',
            })}
        >
            <input {...getInputProps()} />
            <CloudUploadIcon color="primary" sx={{ fontSize: 48, mb: 1, pointerEvents: 'none' }} />
            <Typography variant="subtitle1" fontWeight={600} sx={{ pointerEvents: 'none' }}>
                {title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ pointerEvents: 'none' }}>
                {isDragActive ? activeHint : hint}
            </Typography>
        </Box>
    );
};
